import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { LoginRequest, LoginResponse } from '../types';

interface AuthContextType {
  isAuthenticated: boolean;
  guestName: string | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [guestName, setGuestName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const storedName = localStorage.getItem('guestName');
    
    if (token && storedName) {
      setIsAuthenticated(true);
      setGuestName(storedName);
    }
    setLoading(false);
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      const response = await api.post<LoginResponse>('/auth/login', credentials);
      const { token, guest } = response.data;
      
      localStorage.setItem('authToken', token);
      const fullName = `${guest.firstName} ${guest.lastName}`;
      localStorage.setItem('guestName', fullName);
      
      setIsAuthenticated(true);
      setGuestName(fullName);
    } catch (error) {
      throw new Error('Invalid credentials');
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('guestName');
    setIsAuthenticated(false);
    setGuestName(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, guestName, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
